import { presenceState, type Participant } from '@databases/presence';
import { proxy, subscribe } from 'valtio';

import { telepointerColors } from '@canvas/constants/collaborator/telepointer';
import { AI_AGENT_USER, IdentityKind } from '@canvas/model/identity';

export const getProcessedValueKey = (fieldId: string, entryId: string): string =>
  `${fieldId}-${entryId}`;

// Read-only public interface
export interface ReadonlyUIStreamingState {
  readonly streamingKind: 'aifc' | 'ai-field';
  readonly activeAiFieldCell: { fieldId: string; entryId: string } | null;
  readonly entryIdSelectedForProcessing: string | undefined;
  readonly fieldIdSelectedForProcessing: string | undefined;
  readonly fieldsAreFinishedStreaming: boolean;
  readonly isSingleFieldStreaming: boolean | undefined;
  readonly firstLayoutProcessed: boolean;
  readonly processedValues: ReadonlySet<string>;
  readonly addProcessedValue: (fieldId: string, entryId: string) => void;
  readonly currentFieldOperation: 'delete' | undefined;
  readonly currentEntryOperation: 'edit' | 'delete' | undefined;
}

// Internal mutable interface for StreamingStateManager
export interface MutableUIStreamingState {
  streamingKind: 'aifc' | 'ai-field';
  activeAiFieldCell: { fieldId: string; entryId: string } | null;
  entryIdSelectedForProcessing: string | undefined;
  fieldIdSelectedForProcessing: string | undefined;
  fieldsAreFinishedStreaming: boolean;
  isSingleFieldStreaming: boolean | undefined;
  firstLayoutProcessed: boolean;
  processedValues: Set<string>;
  addProcessedValue: (fieldId: string, entryId: string) => void;
  currentFieldOperation: 'delete' | undefined;
  currentEntryOperation: 'edit' | 'delete' | undefined;
}

const _internalState = proxy<MutableUIStreamingState>({
  streamingKind: 'aifc',
  activeAiFieldCell: null,
  entryIdSelectedForProcessing: undefined,
  fieldIdSelectedForProcessing: undefined,
  fieldsAreFinishedStreaming: false,
  isSingleFieldStreaming: undefined,
  firstLayoutProcessed: false,
  processedValues: new Set<string>(),
  addProcessedValue(fieldId: string, entryId: string) {
    this.processedValues.add(getProcessedValueKey(fieldId, entryId));
  },
  currentFieldOperation: undefined,
  currentEntryOperation: undefined
});

export const aiFirstCreationStreamingState: ReadonlyUIStreamingState = _internalState;

/**
 * INTERNAL USE ONLY
 *
 * This function provides mutable access to the streaming state.
 * Should ONLY be used by StreamingStateManager.
 *
 * @internal
 */
export function __UNSAFE_getMutableStreamingState(): MutableUIStreamingState {
  return _internalState;
}

const ROVO_PARTICIPANT = {
  identity: {
    ...AI_AGENT_USER,
    accountId: 'database-ui-streaming-agent',
    displayName: 'Rovo'
  },
  colorToken: telepointerColors[0].telepointerColorToken // this will be replaced by a gradient
} satisfies Participant;

export const DATABASE_BE_STREAMING_PARTICIPANT = {
  identity: {
    ...AI_AGENT_USER,
    accountId: 'database-be-streaming-agent',
    displayName: 'Rovo'
  },
  colorToken: telepointerColors[0].telepointerColorToken
} satisfies Participant;

/**
 * Recognizes both legacy streaming presence and owner-scoped agent presence.
 */
export function isBackendAgentStreamingParticipant(participant: Participant): boolean {
  const { accountId, type } = participant.identity;
  const baseAccountId = DATABASE_BE_STREAMING_PARTICIPANT.identity.accountId;

  return (
    type === IdentityKind.AiAgent &&
    (accountId === baseAccountId || accountId.startsWith(`${baseAccountId}:`))
  );
}

/**
 * Bridges Rovo AI streaming state to the presence store: while a field or entry
 * is being processed by the teammate, the Rovo participant is shown there.
 * AI fields use their own loading UI instead of presence. Called once at the
 * app composition root. Returns an unsubscribe function.
 */
export function startRovoStreamingPresence(
  store: Pick<
    typeof presenceState,
    'addParticipant' | 'removeParticipant'
  > = presenceState
): () => void {
  const syncPresence = () => {
    const entryId = aiFirstCreationStreamingState.entryIdSelectedForProcessing;
    const fieldId = aiFirstCreationStreamingState.fieldIdSelectedForProcessing;

    if (aiFirstCreationStreamingState.streamingKind === 'ai-field') {
      store.removeParticipant(ROVO_PARTICIPANT);
    } else if (entryId) {
      store.addParticipant({ entryId }, ROVO_PARTICIPANT);
    } else if (fieldId) {
      store.addParticipant({ fieldId }, ROVO_PARTICIPANT);
    } else {
      store.removeParticipant(ROVO_PARTICIPANT);
    }
  };

  const unsubscribe = subscribe(_internalState, syncPresence);
  syncPresence();

  return () => {
    unsubscribe();
    store.removeParticipant(ROVO_PARTICIPANT);
  };
}
